<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import RoomieDivider from '@lahaus-roomie/roomie/src/components/RoomieDivider/index.vue'

import type { Props } from './types'
import { getUnitsInfo } from './utils'

import TypologyCarrousel from '@/components/PageQuotationPlan/TypologyCarrousel/index.vue'
import QuotationPlanFeeSimulator from '@/components/QuotationPlan/FeeSimulator/index.vue'
import AppTablePayment from '@/components/App/Table/Payment/index.vue'
import TypologySlider from '@/components/PageQuotationPlan/TypologySlider/index.vue'
import { getProductTypeLabel } from '@/utils/project'

const { t } = useI18n()

const props = withDefaults(defineProps<Props>(), {
  showUnitsLink: false
})

const route = useRoute()

const typologies = ref([...props.typologies]
  .sort((typologyA, typologyB) => typologyA.min_area - typologyB.min_area)
  .filter(typology => typology.min_unit_code))
const slug = route.params.slug as string

const selectedUnitCode = ref<string>(typologies.value[0].min_unit_code)

const { data: unitsData, pending, error } = await useAsyncData('unitsInfo', () => {
  return getUnitsInfo(props.listingId, selectedUnitCode.value, useNuxtApp())
}, {
  watch: [selectedUnitCode]
})

if (error.value) {
  throw createError({
    statusCode: 503,
    statusMessage: 'Not payment plan found',
    fatal: true
  })
}

const selectedTypology = computed(() => {
  return typologies.value.find(typology => typology.min_unit_code === selectedUnitCode.value)
})

const quotationPlan = computed(() => unitsData.value?.quotationPlan.quotation_plans?.slice(0, 2))

const goToDetailsPage = async (paymentPlanId: number) => {
  const unitCode = selectedUnitCode.value
  await navigateTo(`/planes-de-pago/${slug}/${unitCode}-${paymentPlanId}`)
}

const setTypologyCode = (currentCode: string) => {
  selectedUnitCode.value = currentCode
}

const scrollTop = ref(0)
const principalNavbarVisible = ref(true)

const handleTabsPosition = () => {
  const currentY = window.scrollY

  if (currentY >= scrollTop.value) principalNavbarVisible.value = false
  else principalNavbarVisible.value = true

  scrollTop.value = currentY
}

const monthlyPaymentValueExists = computed(() => {
  return !!quotationPlan.value?.[0]?.initial_plan_items?.monthly_payment_price?.value
})

onMounted(() => {
  window.addEventListener('scroll', () => {
    handleTabsPosition()
  })
})

</script>

<script lang="ts">
export default {
  name: 'QuotationPlanTypologies'
}
</script>

<template>
  <section
    class="quotation-plan-typologies relative">
    <RoomieDivider
      class="!bg-carbon-light-50" />

    <header class="py-16">
      <h3 class="text-18 md:text-24 text-carbon-800 font-semibold">
        {{ t('title', { type: getProductTypeLabel(listingType, true, false, t).toLowerCase() }) }}
      </h3>

      <p class="text-12 text-carbon-600 mt-8">
        {{ t('disclaimer') }}
      </p>
    </header>

    <ClientOnly>
      <TypologySlider
        class="sticky top-20 md:top-24 left-0 right-0 z-20 overflow-y-hidden transition-all duration-200 mx-auto mb-24 mb-0 py-24"
        type="sticky"
        :typologies
        :listing-type
        :current-typology-code="selectedUnitCode"
        @selected-typology-code="setTypologyCode" />
    </ClientOnly>

    <div
      class="flex flex-col md:grid gap-16 lg:grid-cols-2 h-auto">
      <div
        class="rounded-md border border-carbon-light-50 px-16 py-16 lg:py-24 lg:px-24"
        :class="{ 'blur-sm': pending }">
        <div
          v-if="monthlyPaymentValueExists"
          class="flex flex-col gap-8 h-full">
          <QuotationPlanFeeSimulator
            :listing-id
            :unit-code="selectedUnitCode"
            :quotation-plan="quotationPlan[0]"
            @open-details="goToDetailsPage">
            <slot name="under-simulator" />
          </QuotationPlanFeeSimulator>

          <slot name="contact-form" />
        </div>

        <AppTablePayment
          v-else
          id="pd-quotation-plan-table"
          class="flex flex-col h-full justify-between"
          :quotation-plans="quotationPlan"
          @open-details="goToDetailsPage">
          <template #contact-form>
            <slot name="contact-form" />
          </template>
        </AppTablePayment>
      </div>

      <TypologyCarrousel
        :key="`typology-carrousel-${selectedUnitCode}`"
        class="max-h-[480px]"
        :class="{ 'blur-sm': pending }"
        :pictures="selectedTypology?.pictures || []"
        :show-units-link="showUnitsLink"
        :typology-name="selectedTypology?.name || ''" />
    </div>
  </section>
</template>

<style lang="scss">
@import "@lahaus-roomie/roomie/src/components/RoomieTabs/index";

.quotation-plan-typologies {
  &-images {
    @media (width >= 1080px) {
      width: 373px;
    }
  }

  &__form {
    @media (width >= 1024px) {
      min-width: 400px;
    }
  }

  .app-slider {
    &__button {
      @apply absolute;

      &--left {
        left: 0;
      }

      &--right {
        right: 0;
      }
    }
  }

  .app-contact-form {
    &__title,
    &-loading-text,
    &-success-message {
      font-size: 2rem !important;
      line-height: 1.5 !important;
    }

    &-loading,
    &-success-container {
      @apply bg-white;
    }

    &-loading {
      @apply flex flex-col-reverse;
    }

    &-success-icon {
      @apply flex justify-center;
    }

    &-success-message {
      @apply text-center;
    }
  }
}
</style>

<i18n src="./i18n.json" lang="json" />
